export const translations = {
  en: {
    nav: {
      home: 'Home',
      projects: 'Projects',
      skills: 'Skills',
      about: 'About',
      contact: 'Contact',
      downloadCV: 'Download CV',
    },
    home: {
      title: 'Mechanical Design & Simulation Engineer',
      subtitle: 'FEM-driven design, CAD development, and engineering problem-solving',
      value:
        '"I design and validate mechanical systems using CAD and FEM to improve performance and reliability."',
      intro:
        'M.Sc. Automotive Engineering with experience in mechanical design, FEM / CFD simulation and Python-based automation.',
      badge: 'Design & Simulation',
      location: 'Based in',
      available: 'Available for full-time roles',
      availableLong:
        'Open to opportunities in Mechanical Design, Simulation, and Product Development.',
      heroCTA: 'View Projects',
      heroSecondary: 'Get in Touch',
      toolsLabel: 'Core tools',
      competenciesTitle: 'Core Competencies',
      competenciesSub: 'Engineering capabilities applied across real development projects.',
      featuredTitle: 'Selected Projects',
      featuredSub: 'A chronological overview of simulation-driven engineering work.',
      viewAll: 'View all projects',
      focusTitle: 'Engineering Focus',
      focusText:
        'My work centres on mechanical design, FEM/CFD validation and efficient design iteration — always anchored in measurable outcomes relevant to automotive development.',
    },
    projects: {
      title: 'Projects',
      subtitle:
        'Engineering case studies structured around problem, approach and measurable outcome.',
      sortBy: 'Sort by',
      newest: 'Newest first',
      oldest: 'Oldest first',
      filterAll: 'All',
      readCase: 'Read case study',
    },
    projectDetail: {
      back: 'Back to projects',
      context: 'Context',
      problem: 'Problem Statement',
      objective: 'Objective',
      approach: 'Approach & Methodology',
      tools: 'Tools & Technologies',
      results: 'Results & Outcomes',
      learnings: 'Key Takeaway',
      conclusion: 'Conclusion',
      media: 'Media',
      next: 'Next project',
      prev: 'Previous project',
      escHint: 'Press ESC to exit',
    },
    skills: {
      title: 'Skills',
      subtitle: 'Technical toolset and engineering knowledge areas.',
      design: 'Design',
      simulation: 'Simulation',
      engineering: 'Engineering',
      programming: 'Programming',
      soft: 'Soft Skills',
      languages: 'Languages',
    },
    about: {
      title: 'About',
      subtitle: 'Engineering background and professional objective.',
      profile: 'Profile',
      education: 'Education',
      interests: 'Engineering Interests',
      objective: 'Career Objective',
      objectiveText:
        'Seeking a development engineering role in the automotive industry where I can contribute to simulation-driven design of fluid and mechanical systems — translating physical phenomena into robust, manufacturable components.',
      interestsList: [
        'Automotive thermal and fluid systems',
        'Structural simulation under coupled loads',
        'Design automation and parameter studies',
        'Sustainable mobility (EV cooling, lightweight design)',
      ],
    },
    contact: {
      title: 'Contact',
      subtitle:
        'Interested in collaborating on an automotive engineering challenge? Reach out directly or send a message below.',
      name: 'Name',
      email: 'Email',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending…',
      placeholderName: 'Your full name',
      placeholderEmail: 'you@example.com',
      placeholderSubject: 'Project enquiry, role opportunity…',
      placeholderMessage: 'Write your message…',
      success: 'Message sent. Thank you — I will get back to you shortly.',
      error: 'Something went wrong. Please try again or email directly.',
      directEmail: 'Email',
      phone: 'Mobile',
      linkedin: 'LinkedIn',
      downloadCV: 'Download CV (PDF)',
      cvNote: 'CV will open in a new tab',
    },
    footer: {
      tagline: 'Automotive development engineer — designing, simulating, iterating.',
      rights: 'All rights reserved.',
      nav: 'Navigation',
      connect: 'Connect',
      developedBy: 'Crafted by',
    },
    common: {
      year: 'Year',
      tools: 'Tools',
      languageLabel: 'Language',
      featured: 'Featured',
      langPrompt: 'This website is also available in German. Switch language at the top.',
      langPromptCTA: 'Zur deutschen Version',
      close: 'Close',
    },
    cta: {
      headline: 'Let\u2019s build something together',
      line: 'Open to opportunities in Mechanical Design, Simulation, and Product Development.',
      note: 'I enjoy discussing my projects and exchanging ideas with fellow engineers.',
      downloadCV: 'Download CV (PDF)',
      emailMe: 'Email me',
    },
    qr: {
      title: 'Scan to view portfolio',
      hint: 'Open this portfolio on any device by scanning the QR code.',
    },
  },
  de: {
    nav: {
      home: 'Start',
      projects: 'Projekte',
      skills: 'Kenntnisse',
      about: 'Über mich',
      contact: 'Kontakt',
      downloadCV: 'Lebenslauf',
    },
    home: {
      title: 'Konstruktion & Simulation',
      subtitle: 'FEM-basierte Entwicklung, CAD-Konstruktion und technische Problemlösung',
      value:
        '"Ich entwickle und validiere mechanische Systeme mithilfe von CAD und FEM zur Verbesserung von Leistung und Zuverlässigkeit."',
      intro:
        'M.Sc. Fahrzeugtechnik mit Erfahrung in Konstruktion, FEM/CFD-Simulation und Python-basierter Automatisierung.',
      badge: 'Konstruktion & Simulation',
      location: 'Standort',
      available: 'Verfügbar für Vollzeitstellen',
      availableLong:
        'Offen für Positionen im Bereich Konstruktion, Simulation und Produktentwicklung.',
      heroCTA: 'Projekte ansehen',
      heroSecondary: 'Kontakt aufnehmen',
      toolsLabel: 'Werkzeuge',
      competenciesTitle: 'Kernkompetenzen',
      competenciesSub:
        'Ingenieurtechnische Fähigkeiten, eingesetzt in realen Entwicklungsprojekten.',
      featuredTitle: 'Ausgewählte Projekte',
      featuredSub:
        'Chronologischer Überblick simulationsgetriebener Ingenieurarbeit.',
      viewAll: 'Alle Projekte anzeigen',
      focusTitle: 'Entwicklungsschwerpunkt',
      focusText:
        'Mein Arbeitsschwerpunkt liegt auf mechanischer Konstruktion, FEM/CFD-Validierung und effizienter Konstruktionsiteration – stets verankert in messbaren Ergebnissen mit Relevanz für die Automobilentwicklung.',
    },
    projects: {
      title: 'Projekte',
      subtitle:
        'Ingenieurtechnische Fallstudien, strukturiert nach Problem, Ansatz und messbarem Ergebnis.',
      sortBy: 'Sortieren nach',
      newest: 'Neueste zuerst',
      oldest: 'Älteste zuerst',
      filterAll: 'Alle',
      readCase: 'Fallstudie lesen',
    },
    projectDetail: {
      back: 'Zurück zu den Projekten',
      context: 'Kontext',
      problem: 'Problemstellung',
      objective: 'Zielsetzung',
      approach: 'Vorgehensweise & Methodik',
      tools: 'Werkzeuge & Technologien',
      results: 'Ergebnisse',
      learnings: 'Kernaussage',
      conclusion: 'Fazit',
      media: 'Medien',
      next: 'Nächstes Projekt',
      prev: 'Vorheriges Projekt',
      escHint: 'ESC zum Schließen drücken',
    },
    skills: {
      title: 'Kenntnisse',
      subtitle: 'Technisches Werkzeugset und ingenieurwissenschaftliche Wissensbereiche.',
      design: 'Konstruktion',
      simulation: 'Simulation',
      engineering: 'Ingenieurwesen',
      programming: 'Programmierung',
      soft: 'Soft Skills',
      languages: 'Sprachen',
    },
    about: {
      title: 'Über mich',
      subtitle: 'Ingenieurtechnischer Hintergrund und berufliche Zielsetzung.',
      profile: 'Profil',
      education: 'Ausbildung',
      interests: 'Fachliche Interessen',
      objective: 'Berufliches Ziel',
      objectiveText:
        'Ich strebe eine Position als Entwicklungsingenieur in der Automobilindustrie an, in der ich zur simulationsgetriebenen Auslegung von Fluid- und mechanischen Systemen beitragen kann – mit dem Anspruch, physikalische Phänomene in robuste, fertigungsgerechte Komponenten zu überführen.',
      interestsList: [
        'Thermische und strömungstechnische Systeme im Fahrzeug',
        'Strukturmechanische Analyse unter gekoppelten Lastfällen',
        'Konstruktionsautomatisierung und Parameterstudien',
        'Nachhaltige Mobilität – EV-Kühlung und Leichtbau',
      ],
    },
    contact: {
      title: 'Kontakt',
      subtitle:
        'Interesse an einer Zusammenarbeit bei einer ingenieurtechnischen Fragestellung? Gerne direkt kontaktieren oder eine Nachricht über das Formular senden.',
      name: 'Name',
      email: 'E-Mail',
      subject: 'Betreff',
      message: 'Nachricht',
      send: 'Nachricht senden',
      sending: 'Wird gesendet…',
      placeholderName: 'Ihr vollständiger Name',
      placeholderEmail: 'ihre@email.de',
      placeholderSubject: 'Projektanfrage, Stellenangebot …',
      placeholderMessage: 'Ihre Nachricht …',
      success: 'Nachricht gesendet. Vielen Dank – ich melde mich zeitnah zurück.',
      error: 'Es ist ein Fehler aufgetreten. Bitte erneut versuchen oder direkt per E-Mail kontaktieren.',
      directEmail: 'E-Mail',
      phone: 'Mobiltelefon',
      linkedin: 'LinkedIn',
      downloadCV: 'Lebenslauf herunterladen (PDF)',
      cvNote: 'Der Lebenslauf öffnet sich in einem neuen Tab',
    },
    footer: {
      tagline: 'Entwicklungsingenieur Automobiltechnik – konstruieren, simulieren, iterieren.',
      rights: 'Alle Rechte vorbehalten.',
      nav: 'Navigation',
      connect: 'Kontakt',
      developedBy: 'Gestaltet von',
    },
    common: {
      year: 'Jahr',
      tools: 'Werkzeuge',
      languageLabel: 'Sprache',
      featured: 'Ausgewählt',
      langPrompt: 'Diese Website ist auch auf Englisch verfügbar. Sprache oben wechseln.',
      langPromptCTA: 'Switch to English',
      close: 'Schließen',
    },
    cta: {
      headline: 'Lassen Sie uns zusammenarbeiten',
      line: 'Offen für Positionen im Bereich Konstruktion, Simulation und Produktentwicklung.',
      note: 'Ich freue mich über den Austausch zu meinen Projekten.',
      downloadCV: 'Lebenslauf herunterladen (PDF)',
      emailMe: 'E-Mail senden',
    },
    qr: {
      title: 'Portfolio per QR-Code öffnen',
      hint: 'Diese Portfolio-Seite mit einem beliebigen Gerät über den QR-Code öffnen.',
    },
  },
};
